
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminPermissionsService } from '@/services/adminPermissionsService';
import { AdminPermissionUpdate } from '@/types/adminPermissions';
import { toast } from 'sonner';
import { serviceLogger } from '@/utils/debug';

export const useAdminPermissions = () => {
  const queryClient = useQueryClient();

  const {
    data: adminUsers = [],
    isLoading: isLoadingUsers,
    error: usersError
  } = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminPermissionsService.fetchAdminUsers
  });

  const updatePermissionMutation = useMutation({
    mutationFn: (update: AdminPermissionUpdate) =>
      adminPermissionsService.updateAdminPermission(update),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['admin-permissions', variables.admin_profile_id]
      });
      toast.success('Permission updated successfully');
    },
    onError: (error) => {
      serviceLogger.error('Error updating permission:', error);
      serviceLogger.captureError(error);
      toast.error('Failed to update permission');
    }
  });

  return {
    adminUsers,
    isLoadingUsers,
    usersError,
    updatePermission: updatePermissionMutation.mutate,
    isUpdatingPermission: updatePermissionMutation.isPending
  };
};

// Separate hook for fetching admin permissions by ID
export const useAdminPermissionsById = (adminProfileId: string) => {
  return useQuery({
    queryKey: ['admin-permissions', adminProfileId],
    queryFn: () => adminPermissionsService.fetchAdminPermissions(adminProfileId),
    enabled: !!adminProfileId
  });
};

export const useCurrentUserPermissions = () => {
  return useQuery({
    queryKey: ['current-user-permissions'],
    queryFn: adminPermissionsService.fetchCurrentUserPermissions
  });
};
